import { useEffect, useState } from "react";
import { CheckBadgeIcon, ChevronLeftIcon, ChevronRightIcon, MapPinIcon, ChevronRightIcon as RightIcon } from '@heroicons/react/20/solid';
import { addDays, format } from 'date-fns';
import { ArrowLeftCircleIcon, ArrowPathIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { get } from "lodash";
import { TruckIcon } from "@heroicons/react/24/outline";

export default function Travel() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(null);
  const [postcode, setPostcode] = useState(null);
  const [geoLocater, setGeoLocater] = useState(false);
  const [clockIn, setClockIn] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleDateChange = (direction) => {
    const newDate = addDays(currentDate, direction === 'next' ? 1 : -1);
    setCurrentDate(newDate);
  };

  const getMeetings = async () => {
    setLoading(true);
    const formattedDate = format(currentDate, 'yyyy-MM-dd');
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/travel/${formattedDate}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const response2 = await fetch("https://api.insightos.co.uk/api/company", {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response2.ok) throw new Error('Company fetch failed');
      const data2 = await response2.json();
      setPostcode(data2.data[0].Address);
      if (!response.ok) throw new Error('Travel fetch failed');
      const data = await response.json();
      setMeetings(data.data);
      setClockIn(data.clockIn[0]?.MeetingSequence ?? null);
    } catch (err) {
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { getMeetings(); }, [currentDate]);

  const handleDeparted = (id, number) => {
    const updateLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            try {
              const response = await fetch("https://api.insightos.co.uk/api/updatelocation", {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ latitude, longitude, number })
              });
              await response.json();
              getMeetings();
            } catch (error) {}
          },
          () => {}
        );
      }
    };
    updateLocation();
  };

  return (
    <div data-component="travel-planner" className="min-h-screen bg-white px-2 py-4 flex flex-col items-center">
      <div className="w-full max-w-2xl mx-auto">
        <header className="flex flex-col sm:flex-row items-center justify-between bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center w-full justify-between gap-2 sm:gap-6 ">
            <button
              data-component="travel-date-nav"
              data-feature="previous-day"
              onClick={() => handleDateChange('prev')}
              className="flex items-center justify-center w-12 h-12 rounded-full text-white bg-gradient-to-r from-indigo-600 to-cyan-600 shadow-lg hover:from-indigo-700 hover:to-cyan-700 text-base font-bold active:scale-95 transition-all"
            >
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
            </button>
            <h1 className="text-lg sm:text-xl text-white text-center flex-1 py-2">
              {format(currentDate, "EEEE, MMMM d, yyyy")}
            </h1>
            <button
              data-component="travel-date-nav"
              data-feature="next-day"
              onClick={() => handleDateChange('next')}
              className="flex items-center justify-center w-12 h-12 rounded-full text-white bg-gradient-to-r from-indigo-600 to-cyan-600 shadow-lg hover:from-indigo-700 hover:to-cyan-700 text-base font-bold active:scale-95 transition-all"
            >
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </header>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="animate-spin h-10 w-10 text-cyan-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="text-cyan-500 font-semibold text-lg">Loading travel plans...</span>
          </div>
        ) : !open ? (
          meetings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <svg className="h-10 w-10 text-gray-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path fill="currentColor" d="M8 12h8v2H8z" />
              </svg>
              <span className="text-gray-500 font-semibold text-lg">No travel plans today</span>
            </div>
          ) : (
            <ul role="list" className="space-y-6">
              {meetings.map((meeting) => (
                <li key={meeting.id} className="relative flex flex-col sm:flex-row justify-between bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 rounded-2xl shadow-lg border border-white/20 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row flex-1 gap-4">
                    <button
                      data-component="travel-direction-action"
                      data-feature="open-maps"
                      onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${postcode}&destination=${meeting.Postcode}`, "_blank")}
                      type="button"
                      className="flex items-center justify-center w-12 h-12 rounded-full hover:scale-105 bg-gradient-to-br from-indigo-600 to-cyan-600 text-white shadow-lg border border-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 mb-3 sm:mb-0"
                    >
                      <MapPinIcon aria-hidden="true" className="h-6 w-6" />
                    </button>
                    <div className="flex-1">
                      <p className="text-base font-bold text-white mb-1">{meeting.Name}</p>
                      <p className="text-xs text-cyan-200">Client: {meeting.ClientName}</p>
                      <p className="text-xs text-blue-200">Attendees: {meeting.Attendees}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                        <p className="text-xs text-gray-300">Leaving: <span className="font-bold text-emerald-400">{meeting.LeavingTime ? format(new Date(`${format(currentDate, 'yyyy-MM-dd')}T${meeting.LeavingTime}`), 'h:mm a') : meeting.LeavingTime}</span></p>
                        <p className="text-xs text-gray-300">Return: <span className="font-bold text-cyan-400">{meeting.ReturnTime ? format(new Date(`${format(currentDate, 'yyyy-MM-dd')}T${meeting.ReturnTime}`), 'h:mm a') : meeting.ReturnTime}</span></p>
                      </div>
                      <p className="text-xs mt-3 text-blue-200">{meeting.Address}</p>
                      <p className="mt-1 text-xs text-cyan-300 font-bold">{meeting.Postcode}, {meeting.State}</p>
                      <p className="mt-1 text-xs text-cyan-300 font-bold">Start: {meeting.Time}</p>
                      <p className="mt-1 text-xs text-blue-200">{meeting.TotalReturnDistance} - RT</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {clockIn === null && (
                          <button
                            data-component="travel-status-action"
                            data-feature="departing"
                            onClick={()=> handleDeparted(meeting.id, 1)}
                            type="button"
                            className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-600 px-3 py-2 text-sm font-bold text-white shadow-lg hover:from-emerald-600 hover:to-cyan-700 active:scale-95 transition-all"
                          >
                            Departing
                            <TruckIcon aria-hidden="true" className="ml-2 h-5 w-5 animate-bounce" />
                          </button>
                        )}
                        {clockIn === 1 && (
                          <button
                            data-component="travel-status-action"
                            data-feature="arrived"
                            onClick={()=> handleDeparted(meeting.id, 2)}
                            type="button"
                            className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 px-3 py-2 text-sm font-bold text-white shadow-lg hover:from-blue-600 hover:to-cyan-600 active:scale-95 transition-all"
                          >
                            Arrived
                            <CheckBadgeIcon aria-hidden="true" className="ml-2 h-5 w-5" />
                          </button>
                        )}
                        {clockIn === 2 && (
                          <button
                            data-component="travel-status-action"
                            data-feature="returning"
                            onClick={()=> handleDeparted(meeting.id, 2)}
                            type="button"
                            className="inline-flex items-center gap-x-1.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 px-3 py-2 text-sm font-bold text-white shadow-lg hover:from-indigo-600 hover:to-cyan-600 active:scale-95 transition-all"
                          >
                            Returning
                            <ArrowPathIcon aria-hidden="true" className="ml-2 h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="p-6 bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20">
            <button
              data-component="travel-back-action"
              data-feature="close-details"
              onClick={() => setOpen(null)}
              className="mb-4 text-cyan-400 font-bold hover:text-white transition"
            >
              Go Back
            </button>
            <button
              data-component="travel-direction-action"
              data-feature="open-detail-maps"
              onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${postcode}&destination=${open.Postcode}`, "_blank")}
              className="inline-flex justify-center rounded-xl bg-gradient-to-r from-indigo-600 to-cyan-600 px-4 py-3 text-base font-bold text-white shadow-lg hover:from-indigo-700 hover:to-cyan-700 transition-all"
            >
              Open Directions in Google Maps
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
